import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { SHARP_ICON_NAMES, type SharpIconName } from './sharp-icon-names';
export { SHARP_ICON_NAMES };
export type { SharpIconName };

export type SharpIconProps = React.ComponentProps<'svg'> &
  VariantProps<typeof sharpIconVariants> & {
    /** The icon to display. Each value maps to a tool category glyph. */
    name: SharpIconName;
    /** `inverted` uses the inverted sharp-icon tokens, for an icon on the opposite surface. */
    surface?: 'default' | 'inverted';
  };

export const sharpIconVariants = cva(
  [
    'group/sharp-icon',
    'text-inherit',
    'inline-flex',
    'aspect-ratio-square',
    'items-center',
    'justify-center',
    'transition-colors',
    'shrink-0',
    'outline-none',
  ],
  {
    variants: {
      /** The size of the icon. */
      size: {
        sm: 'size-4 min-w-4 min-h-4 max-w-4 max-h-4', // 16px
        md: 'size-6 min-w-6 min-h-6 max-w-6 max-h-6', // 24px
        lg: 'size-8 min-w-8 min-h-8 max-w-8 max-h-8', // 32px
        xl: 'size-10 min-w-10 min-h-10 max-w-10 max-h-10', // 40px
      },
      /** When true, path color tokens switch to their selected-default counterparts. */
      selected: {
        true: '',
        false: '',
      },
    },
  },
);

/** Switches `fill-sharp-icon-background` ↔ `fill-selected-default-background`, or the inverted token. */
export const sharpIconBackgroundVariants = cva('', {
  variants: {
    selected: {
      true: 'fill-selected-default-background',
      false: '',
    },
    /** Default tokens, or the inverted sharp-icon tokens for an opposite surface. */
    surface: {
      default: '',
      inverted: '',
    },
  },
  compoundVariants: [
    { selected: false, surface: 'default', className: 'fill-sharp-icon-background' },
    { selected: false, surface: 'inverted', className: 'fill-sharp-icon-inverted-background' },
  ],
  defaultVariants: { selected: false, surface: 'default' },
});

/** Switches `fill-sharp-icon-duotone-secondary` ↔ `fill-selected-default-stroke`, or the inverted token. */
export const sharpIconDuotoneSecondaryVariants = cva('', {
  variants: {
    selected: {
      true: 'fill-selected-default-background-active',
      false: '',
    },
    /** Default tokens, or the inverted sharp-icon tokens for an opposite surface. */
    surface: {
      default: '',
      inverted: '',
    },
  },
  compoundVariants: [
    { selected: false, surface: 'default', className: 'fill-sharp-icon-duotone-secondary' },
    { selected: false, surface: 'inverted', className: 'fill-sharp-icon-inverted-duotone-secondary' },
  ],
  defaultVariants: { selected: false, surface: 'default' },
});

/** Switches `stroke-sharp-icon-duotone-primary` ↔ `stroke-selected-default-icon`, or the inverted token. */
export const sharpIconDuotonePrimaryVariants = cva('', {
  variants: {
    selected: {
      true: 'stroke-selected-default-icon',
      false: '',
    },
    /** Default tokens, or the inverted sharp-icon tokens for an opposite surface. */
    surface: {
      default: '',
      inverted: '',
    },
  },
  compoundVariants: [
    { selected: false, surface: 'default', className: 'stroke-sharp-icon-duotone-primary' },
    { selected: false, surface: 'inverted', className: 'stroke-sharp-icon-inverted-duotone-primary' },
  ],
  defaultVariants: { selected: false, surface: 'default' },
});

/**
 * Inline duotone SVG icon.
 *
 * ### When to use
 * - Generally alongside a tool name in cards, list items, and table rows to provide quick visual identification of a tool's category
 * - Any surface where a compact, color-adaptive category glyph is needed
 *
 * ### When NOT to use
 * - For general UI actions or status indicators: use `<Icon>` instead
 * - For arbitrary decorative illustrations — it is generally for representing MagicSchool tool categories
 * - As a standalone decorative element without adjacent visible text — the icon is `role="presentation"` and conveys no label to assistive technology
 *
 * ### API
 * - `name`: The icon to render. See `SHARP_ICON_NAMES` for the full list (required).
 * - `size`: Controls the rendered dimensions. `sm` / `md` / `lg` / `xl` map to progressively larger fixed square sizes. Defaults to `'md'`.
 * - `strokeWidth`: SVG stroke width passed through to the inner paths. Defaults to `1`. Increase for heavier weight at larger sizes.
 * - `surface`: `default` or `inverted`. Use `inverted` on an opposite surface, such as a tooltip.
 *
 * ### Color
 * - Fill and stroke are driven entirely by the `sharp-icon-duotone-primary`, `sharp-icon-duotone-secondary`, and `sharp-icon-background` CSS tokens — do not override colors with `className`.
 * - `surface="inverted"` uses the inverted sharp-icon tokens, for an icon on the opposite surface (a tooltip). Do not re-point the tokens at palette primitives.
 * - The icon inherits `currentColor` for `text-*` utilities applied by a parent, but the token classes take precedence.
 *
 * ### Accessibility
 * - The SVG has `role="presentation"` and is intentionally invisible to screen readers. Always pair with adjacent visible text that identifies the tool or category.
 */
export const SharpIcon: React.FC<SharpIconProps> = ({
  className,
  name,
  size = 'md',
  selected,
  surface = 'default',
  strokeWidth = 1,
  ...props
}) => {
  let pathData = null;
  switch (name) {
    case 'ai-generate-text-spark':
      pathData = (
        <>
          <path
            className={sharpIconBackgroundVariants({ selected, surface })}
            d="M8.611 3.722A3.043 3.043 0 0 0 11.667.667a3.043 3.043 0 0 0 3.055 3.055 3.043 3.043 0 0 0-3.055 3.056A3.043 3.043 0 0 0 8.61 3.722"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M8.611 3.722A3.043 3.043 0 0 0 11.667.667a3.043 3.043 0 0 0 3.055 3.055 3.043 3.043 0 0 0-3.055 3.056A3.043 3.043 0 0 0 8.61 3.722ZM1.89 5.556H8M1.89 14.722H14.11M1.89 11.667H14.11M1.89 8.611H14.11"
          />
        </>
      );
      break;
    case 'backpack':
      pathData = (
        <>
          <path className={sharpIconDuotoneSecondaryVariants({ selected, surface })} d="M3.721 3.721v11.003h8.558V3.72z" />
          <path
            className={sharpIconBackgroundVariants({ selected, surface })}
            d="M1.276 14.724h2.445v-4.89H1.276zM12.279 14.724h2.445v-4.89h-2.445z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M3.721 3.721v11.003h8.558V3.72z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M3.721 5.555v.611a4.279 4.279 0 1 0 8.558 0v-.611M6.166 3.721V3.11a1.834 1.834 0 1 1 3.668 0v.611M7.083 8h1.834M3.721 14.724H1.276v-4.89h2.445M12.279 14.724h2.445v-4.89h-2.445"
          />
        </>
      );
      break;
    case 'book-flip':
      pathData = (
        <>
          <path
            className={sharpIconDuotoneSecondaryVariants({ selected, surface })}
            d="M1.278 12.889V3.11h4.278A2.444 2.444 0 0 1 8 5.555v9.167c0-1.012-.82-1.833-1.833-1.833z"
          />
          <path
            className={sharpIconBackgroundVariants({ selected, surface })}
            d="M12.278 10.444C8.856 10.444 8 13.296 8 14.722V5.555c0-1.425.856-4.277 4.278-4.277z"
          />
          <path
            className={sharpIconDuotoneSecondaryVariants({ selected, surface })}
            d="M14.722 12.889H9.833c-1.012 0-1.833.82-1.833 1.833 0-1.426.856-4.278 4.278-4.278V3.111h2.444z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M1.278 12.889V3.11h4.278A2.444 2.444 0 0 1 8 5.555v9.167c0-1.012-.82-1.833-1.833-1.833z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M12.278 3.111h2.444v9.778H9.833c-1.012 0-1.833.82-1.833 1.833"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M8 14.722c0-1.426.856-4.278 4.278-4.278V1.278C8.856 1.278 8 4.13 8 5.555"
          />
        </>
      );
      break;
    case 'calendar-mark':
      pathData = (
        <>
          <path className={sharpIconDuotoneSecondaryVariants({ selected, surface })} d="M1.887 3.11h12.226v2.445H1.886z" />
          <path className={sharpIconBackgroundVariants({ selected, surface })} d="M1.887 14.113h12.226V5.555H1.886z" />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M1.887 3.11h12.226v11.003H1.886zM1.887 5.555h12.226M4.333 1.276V3.11M11.668 1.276V3.11M3.721 8.611h1.834M3.721 11.056h1.834M7.083 8.611h1.834M10.445 8.611h1.834M7.083 11.056h1.834"
          />
        </>
      );
      break;
    case 'chat-two-bubbles-oval':
      pathData = (
        <>
          <path
            className={sharpIconDuotoneSecondaryVariants({ selected, surface })}
            d="M1.785 8.178c0 1.03.256 2.032.731 2.924l-.448 3.008 3.029-.437q.386.204.798.352a5.65 5.65 0 0 1 7.953-7.952A6.216 6.216 0 0 0 1.785 8.178"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M13.956 6.404a6.215 6.215 0 1 0-11.44 4.698l-.448 3.008 3.029-.437q.324.171.666.303"
          />
          <path className={sharpIconBackgroundVariants({ selected, surface })} d="M13.75 12.299a3.955 3.955 0 1 0-1.642 1.636l1.927.278z" />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M13.75 12.299a3.955 3.955 0 1 0-1.642 1.636l1.927.278z"
          />
        </>
      );
      break;
    case 'clipboard-text-task':
      pathData = (
        <>
          <path className={sharpIconDuotoneSecondaryVariants({ selected, surface })} d="M2.498 2.499h11.003v12.225H2.498z" />
          <path className={sharpIconDuotoneSecondaryVariants({ selected, surface })} d="M5.554 3.721h4.89V1.276h-4.89z" />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M10.445 2.499H13.5v12.225H2.498V2.499h3.057"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M5.554 1.276h4.89v2.445h-4.89zM4.943 8h6.113M4.943 10.445H8"
          />
        </>
      );
      break;
    case 'content-browser-edit':
      pathData = (
        <>
          <path
            className={sharpIconBackgroundVariants({ selected, surface })}
            d="M14.722 7.389h-.462L11.972 5.1 5.556 11.52v.759H1.278V3.722h13.444z"
          />
          <path className={sharpIconDuotoneSecondaryVariants({ selected, surface })} d="M1.278 1.278h13.444v2.444H1.278z" />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M5.556 12.278H1.278v-11h13.444v6.11M14.722 3.722H1.278"
          />
          <path
            className={sharpIconDuotoneSecondaryVariants({ selected, surface })}
            d="m14.417 10.138-2.445-2.444-4.583 4.584v2.444h2.444z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="m14.417 10.138-2.445-2.444-4.583 4.584v2.444h2.444zM3.722 6.167h6.111M3.722 8.611H7.39"
          />
        </>
      );
      break;
    case 'cyborg-2':
      pathData = (
        <>
          <path className={sharpIconDuotoneSecondaryVariants({ selected, surface })} d="M2.146 14.111V6.167h11.612v7.944z" />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M9.222 3.111a1.222 1.222 0 1 1-2.444 0 1.222 1.222 0 0 1 2.444 0ZM8 4.334v1.833M2.146 14.111V6.167h11.611v7.944z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M9.833 10.14a1.833 1.833 0 1 1-3.666 0M6.167 8v1.222M9.833 8v1.222"
          />
        </>
      );
      break;
    case 'heart-support':
      pathData = (
        <>
          <path
            className={sharpIconBackgroundVariants({ selected, surface })}
            d="m7.667 14.667 6.333-2v-.262a1.74 1.74 0 0 0-2.25-1.662L7.667 12v-2l-4.448-.953A2.11 2.11 0 0 0 .667 11.11v3.557z"
          />
          <path
            className={sharpIconDuotoneSecondaryVariants({ selected, surface })}
            d="m11.219 1.886.448.447.447-.447a1.886 1.886 0 1 1 2.667 2.666l-3.114 3.115-3.115-3.115a1.886 1.886 0 0 1 2.667-2.666"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="m11.219 1.886.448.447.447-.447a1.886 1.886 0 1 1 2.667 2.666l-3.114 3.115-3.115-3.115a1.886 1.886 0 0 1 2.667-2.666ZM.167 14.667h7.5l6.333-2v-.262a1.74 1.74 0 0 0-2.25-1.661l-3.968 1.22a.8.8 0 0 1-.236.036H4.333"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M7.667 12v-2l-4.448-.953A2.11 2.11 0 0 0 .667 11.11v.223"
          />
        </>
      );
      break;
    case 'lightbulb':
      pathData = (
        <>
          <path className={sharpIconDuotonePrimaryVariants({ selected, surface })} strokeWidth={strokeWidth} d="M3.416 14.113h6.112" />
          <path
            className={sharpIconDuotoneSecondaryVariants({ selected, surface })}
            d="M9.528 6.472V1.888a4.584 4.584 0 0 1 4.585 4.584z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M9.528 6.472V1.888a4.584 4.584 0 0 1 4.585 4.584z"
          />
          <path
            className={sharpIconBackgroundVariants({ selected, surface })}
            fillRule="evenodd"
            d="M7.694 6.472H6.472V1.888a4.584 4.584 0 0 0-2.445 8.463v1.316h4.89v-1.316a4.6 4.6 0 0 0 1.758-2.045h-2.98z"
            clipRule="evenodd"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M10.697 8.255a4.6 4.6 0 0 1-1.78 2.095v1.318h-4.89V10.35a4.584 4.584 0 0 1 2.445-8.463v4.584h1.231"
          />
          <path className={sharpIconDuotonePrimaryVariants({ selected, surface })} strokeWidth={strokeWidth} d="M1.887 6.472h4.585v5.196" />
        </>
      );
      break;
    case 'notepad-text':
      pathData = (
        <>
          <path className={sharpIconDuotoneSecondaryVariants({ selected, surface })} d="M1.333 2.667h13.334v12H1.333z" />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M4.667.667v4M8 .667v4M11.333.667v4M4 7.334h8M4 10.667h4M1.333 2.667h13.334v12H1.333z"
          />
        </>
      );
      break;
    case 'task-list':
      pathData = (
        <>
          <path className={sharpIconDuotoneSecondaryVariants({ selected, surface })} d="M10.444 1.278H2.5v13.444h11V4.333z" />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M8.716 6.428h2.925M8.716 10.674h2.925M4.343 5.995l1.2 1.186 1.712-2.428M4.343 10.305l1.2 1.187 1.712-2.428"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M10.444 1.278H2.5v13.444h11V4.333z"
          />
        </>
      );
      break;
    case 'toolbox':
      pathData = (
        <>
          <path className={sharpIconDuotoneSecondaryVariants({ selected, surface })} d="M1.333 4.667h13.334v10H1.333z" />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M1.333 4.667h13.334v10H1.333zM1.333 8h13.334M8 8v2M11.333 4.667a3.333 3.333 0 0 0-6.666 0"
          />
        </>
      );
      break;
    case 'web-application-learning':
      pathData = (
        <>
          <path
            className={sharpIconBackgroundVariants({ selected, surface })}
            fillRule="evenodd"
            d="M1.278 1.278h13.444v7.076l-4.278-1.719L4.4 9.065v3.213H1.278z"
            clipRule="evenodd"
          />
          <path
            className={sharpIconDuotoneSecondaryVariants({ selected, surface })}
            d="M14.722 1.278H1.278v2.444h13.444zM8.145 13.565l2.299 1.078 2.303-1.078v-2.07H8.14z"
          />
          <path
            className={sharpIconDuotoneSecondaryVariants({ selected, surface })}
            d="m10.444 8.611-4.21 1.692v.27l4.21 1.692 4.211-1.692v-.27z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M14.722 3.722H1.278M10.444 8.611l-4.21 1.692v.27l4.21 1.692 4.211-1.692v-.27z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="m8.14 11.34.005 2.225 2.299 1.078 2.303-1.078v-2.226M6.233 13.054v-2.48M14.722 8V1.278H1.278v11h3.055M14.722 3.722H1.278"
          />
        </>
      );
      break;
    case 'globe-website-setting':
    default:
      pathData = (
        <>
          <path
            className={sharpIconDuotoneSecondaryVariants({ selected, surface })}
            d="M8 1.276a6.724 6.724 0 0 1 6.715 6.378l-.817.07-.435-.932H9.772l-.437.933-1.024-.09-.583 1.01-.678 1.176-.584 1.01.59.844-.59.844.584 1.01.339.587v.579A6.723 6.723 0 0 1 8 1.275"
          />
          <path
            className={sharpIconBackgroundVariants({ selected, surface })}
            d="m12.66 9.402-.364-.776h-1.357l-.363.777-.405.234-.855-.074-.678 1.175.491.704v.466l-.491.704.678 1.175.855-.074.405.234.363.777h1.357l.363-.777.406-.234.854.074.678-1.175-.49-.703v-.469l.49-.702-.678-1.175-.854.073z"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="m12.66 9.402-.364-.776h-1.357l-.363.777-.405.234-.855-.074-.678 1.175.491.704v.466l-.491.704.678 1.175.855-.074.405.234.363.777h1.357l.363-.777.406-.234.854.074.678-1.175-.49-.703v-.469l.49-.702-.678-1.175-.854.073zM11.618 11.063v1.223M8.622 1.305l1.99 5.472M7.379 1.305 4.944 8l1.445 3.973"
          />
          <path
            className={sharpIconDuotonePrimaryVariants({ selected, surface })}
            strokeWidth={strokeWidth}
            d="M7.389 14.696a6.725 6.725 0 1 1 7.328-7.002M1.276 8H8"
          />
        </>
      );
      break;
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 16 16"
      role="presentation"
      data-spellbook-name="sharp-icon"
      className={cn(sharpIconVariants({ size, selected }), className)}
      {...props}
    >
      {pathData}
    </svg>
  );
};
